import {useAuth} from '@apps-common/auth-react';
import GeoJSON from 'ol/format/GeoJSON';
import {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  handleWorkerMessage,
  resetResults,
  updateStatus,
} from '@src/actions/results';
import {resetSearchPageNumber} from '@src/actions/search';
import useSearchFilters from '@src/hooks/useSearchFilters';
import {getMapFeatureGeometry} from '@src/selectors/map';
import {getSearchItemTypeIds} from '@src/selectors/search';
import {computeGeometryTooLarge} from '@src/utils/geom.js';
import modifyEndDate from '@src/utils/modifyEndDate.js';
import pluckQueryParam from '@src/utils/pluckQueryParam.js';

const formatGeoJSON = new GeoJSON();

const worker = new Worker(new URL('../worker/main.js', import.meta.url), {
  type: 'module',
});

const WorkerManager = () => {
  const auth = useAuth();
  const dispatch = useDispatch();

  const geometry = useSelector(getMapFeatureGeometry);
  const itemTypeIds = useSelector(getSearchItemTypeIds);
  const orderItems = useSelector(state => state.results.orderItems);
  const orderResults = useSelector(state => state.results.orderResults);
  const searchPage = useSelector(state => state.search.page);
  const sortDirection = useSelector(
    state => state.sidebar.resultsSortDirection,
  );
  const viewItems = useSelector(state => state.results.viewItems);
  const viewResults = useSelector(state => state.results.viewResults);
  const featureReference = useSelector(state => state.search.featureReference);

  const {geometryTooLarge} = geometry
    ? computeGeometryTooLarge(formatGeoJSON.readGeometry(geometry))
    : false;

  const ready = !!(geometry && !geometryTooLarge);
  const {searchFilters} = useSearchFilters({useNoEndDate: false});
  const isReady = ready && !!searchFilters && !!geometry;

  useEffect(() => {
    const onWorkerMessage = event => dispatch(handleWorkerMessage(event.data));

    worker.addEventListener('message', onWorkerMessage);

    return () => {
      worker.removeEventListener('message', onWorkerMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!geometry) {
      worker.postMessage({type: 'clear'});
    }
  }, [geometry]);

  // tracks whether items have been orphaned.
  const orphanedItems = Boolean(
    (viewItems.length && !viewResults.length) ||
      (orderItems.length && !orderResults.length),
  );

  // if there is a since query param, we override the filter to include imagery
  // up to today. Also share the date with the worker (see below) so it can
  // mark imagery as new if it is later than that date
  const since = pluckQueryParam('since');

  // Note: this is stringified so useEffect doesn't think it's different when
  // the object changes (which it will every time any of its inputs change,
  // even if the output is the same)
  const filters = useMemo(
    () => JSON.stringify(since ? modifyEndDate(searchFilters) : searchFilters),
    [searchFilters, since],
  );

  useEffect(() => {
    if (isReady) {
      // If we have any orphaned items (those without results), we
      // can reset the results and page index.
      // This check is needed to be able to preserve selected items/results when reloading or
      // sharing an URL.
      if (!orphanedItems) {
        dispatch(resetResults());
        dispatch(resetSearchPageNumber());
      }

      dispatch(updateStatus(true));

      worker.postMessage({type: 'clear'});

      worker.postMessage({
        type: 'request',
        data: {
          featureReference,
          filters: JSON.parse(filters), // See above JSON.stringify call
          geometry,
          itemTypes: itemTypeIds,
          since: since && new Date(since), // See above comment
          sortDirection,
          token: auth.user.accessToken,
        },
      });
    }
  }, [
    auth.user.accessToken,
    dispatch,
    featureReference,
    filters,
    geometry,
    isReady,
    itemTypeIds,
    orphanedItems,
    since,
    sortDirection,
  ]);

  useEffect(() => {
    if (isReady && searchPage > 0) {
      worker.postMessage({type: 'more'});
    }
  }, [isReady, searchPage]);

  return null;
};

export default WorkerManager;
